const SRCSET_WIDTHS = [165, 360, 533, 720, 940, 1066];
const altColor = alt => alt?.split('||').pop()?.toLowerCase().trim();

const desktopMQ = window.matchMedia('(min-width: 769px)');

function generateSrcset(image, widths = SRCSET_WIDTHS) {
  const url = new URL(image.src);
  return widths
    .filter(w => w <= image.width)
    .map(w => {
      url.searchParams.set('width', w);
      return `${url.href} ${w}w`;
    })
    .join(', ');
}

function createImageElement(image, classes, sizes, productTitle) {
  const preview = image.preview_image;
  const img = new Image(preview.width, preview.height);
  img.className = classes;
  img.alt      = image.alt || productTitle;
  img.sizes    = sizes;
  img.src      = preview.src;
  img.srcset   = generateSrcset(preview);
  img.loading  = 'lazy';
  return img;
}

const productCache = new Map();

function getProductData(handle) {
  if (productCache.has(handle)) return productCache.get(handle);

  try {
    const raw = sessionStorage.getItem(`qapc_${handle}`);
    if (raw) {
      const promise = Promise.resolve(JSON.parse(raw));
      productCache.set(handle, promise);
      return promise;
    }
  } catch {}

  const promise = fetch(`${window.Shopify.routes.root}products/${handle}.js`)
    .then(r => r.json())
    .then(data => {
      try { sessionStorage.setItem(`qapc_${handle}`, JSON.stringify(data)); } catch {}
      return data;
    })
    .catch(() => null);

  productCache.set(handle, promise);
  return promise;
}

function checkSwatches() {
  document.querySelectorAll('.js-color-swatches-wrapper').forEach(wrapper => {
    const card   = wrapper.closest('.card-information') || wrapper;
    const handle = wrapper.dataset.product;
    let checkedInput = wrapper.querySelector('.js-color-swatches input:checked');

    if (!checkedInput) {
      const inputs = wrapper.querySelectorAll('.js-color-swatches input');
      if (inputs.length > 0) {
        checkedInput = inputs[0];
        checkedInput.checked = true;
      }
    }

    if (checkedInput) {
      updateSwatchSelection(checkedInput, wrapper);
      updateLowstockBadge(card, checkedInput.dataset.lowStock);
    }

    if (handle && desktopMQ.matches) {
      wrapper.addEventListener('mouseenter', () => {
        const timer = setTimeout(() => populateQuickAddBar(wrapper), 150);
        wrapper.addEventListener('mouseleave', () => clearTimeout(timer), { once: true, passive: true });
      }, { passive: true });
    }

    wrapper.querySelectorAll('.js-color-swatches input').forEach(input => {
      input.closest('.swiper-slide')?.addEventListener('mouseenter', () => {
        if (!handle) return;
        getProductData(handle).then(product => {
          if (!product) return;
          const colorIdx = product.options.findIndex(o => o.name.toLowerCase() === 'color');
          if (colorIdx === -1) return;
          const selectedColor = input.value.toLowerCase().trim();
          const colorKey     = `option${colorIdx + 1}`;
          const colorVariants = product.variants.filter(v =>
            v[colorKey]?.toLowerCase().trim() === selectedColor
          );
          let mediaSrc = colorVariants.find(v => v.featured_media)?.featured_media?.preview_image?.src;
          if (!mediaSrc) {
            const variantIds = new Set(colorVariants.map(v => v.id));
            mediaSrc = product.images?.find(img =>
              img.variant_ids?.some(id => variantIds.has(id))
            )?.src;
          }
          if (!mediaSrc) {
            mediaSrc = product.media?.find(m =>
              m.media_type === 'image' && altColor(m.alt) === selectedColor
            )?.preview_image?.src;
          }
          if (mediaSrc) {
            const preload = new Image();
            preload.src = mediaSrc;
          }
        });
      }, { passive: true });
    });

    wrapper.addEventListener('change', e => {
      const input = e.target.closest('.js-color-swatches input[type="radio"]');
      if (!input || !input.checked) return;
      updateSwatchSelection(input, wrapper);
      if (desktopMQ.matches) populateQuickAddBar(wrapper);
      updateLowstockBadge(card, input.dataset.lowStock);
      updateCardMessage(card, input.dataset.cardMessage);
    }, { passive: true });
  });
}

function updateLowstockBadge(card, lowStockValue) {
  const badge = card.querySelector('.lowstock-badge');
  if (!badge) return;
  const val = (lowStockValue || '').trim();
  badge.textContent    = val;
  badge.style.display  = val ? 'inline-block' : 'none';
}

function updateCardMessage(card, message) {
  const el = card.querySelector('.product__promo_msg');
  if (!el) return;
  const val = (message || '').trim();
  el.textContent = val;
  el.hidden      = !val;
}

function updateSwatchSelection(input, wrapper) {
  const primaryImage   = wrapper.querySelector('.media--first');
  const productCard    = input.closest('.collection-product-card');
  const secondaryImage = productCard?.querySelector('.media--second');
  const handle         = wrapper.dataset.product;

  if (!primaryImage) return;

  const primaryClassName = primaryImage.className;
  const primarySizes     = primaryImage.sizes;

  wrapper.querySelectorAll('.js-color-swatches-link').forEach(link =>
    link.setAttribute('href', input.dataset.variantLink)
  );

  let imageSetSync = false;
  const directSrc = input.dataset.variantImage;
  if (directSrc) {
    try {
      const u = new URL(directSrc);
      const srcset = SRCSET_WIDTHS
        .map(w => { u.searchParams.set('width', w); return `${u.href} ${w}w`; })
        .join(', ');
      u.searchParams.set('width', 600);
      if (u.href !== primaryImage.src) {
        primaryImage.src    = u.href;
        primaryImage.srcset = srcset;
      }
      imageSetSync = true;
    } catch (_) {}
  }

  getProductData(handle).then(product => {
    if (!product) return;

    const colorIdx = product.options.findIndex(o => o.name.toLowerCase() === 'color');
    if (colorIdx === -1) return;

    const selectedColor = input.value.toLowerCase().trim();
    const colorKey      = `option${colorIdx + 1}`;

    const colorVariants = product.variants.filter(v =>
      v[colorKey]?.toLowerCase().trim() === selectedColor
    );

    if (!colorVariants.length) return;

    const swatchVariantId = parseInt(input.dataset.variant || input.dataset.variantId, 10);
    const variant = (swatchVariantId && colorVariants.find(v => v.id === swatchVariantId)) || colorVariants[0];

    const productCard = input.closest('.collection-product-card');
    if (productCard) {
      productCard.setAttribute('data-variant-id', variant.id);
      ['.variant-tag', '.variant-newsize_tag', '.variant-preorder_tag',
       '.new_promo', '.best_promo', '.pref_promo', '.los_promo'].forEach(sel =>
        window.updateVariantTags(productCard, sel, input)
      );
    }

    const card = input.closest('.card-information');
    if (card) {
      const title = card.querySelector('.b4b-title div, .option_name_CTM');
      if (title) title.textContent = input.value;
      updatePricing(card, variant, input.value);
    }

    if (!imageSetSync) {
      const exactIds    = new Set([variant.id]);
      const allColorIds = new Set(colorVariants.map(v => v.id));
      let media = null;

      const byExact = product.images?.find(img =>
        img.variant_ids?.some(id => exactIds.has(id))
      );
      if (byExact) {
        media = { preview_image: byExact, alt: byExact.alt };
      } else {
        const byColor = product.images?.find(img =>
          img.variant_ids?.some(id => allColorIds.has(id))
        );
        if (byColor) media = { preview_image: byColor, alt: byColor.alt };
      }

      if (!media) {
        const byAlt = product.media?.find(m =>
          m.media_type === 'image' && altColor(m.alt) === selectedColor
        );
        if (byAlt) media = byAlt;
      }

      if (!media) {
        const byImgAlt = product.images?.find(img =>
          altColor(img.alt) === selectedColor
        );
        if (byImgAlt) media = { preview_image: byImgAlt, alt: byImgAlt.alt };
      }

      if (media) {
        updateImage(primaryImage, createImageElement(media, primaryClassName, primarySizes, product.title));
      }
    }

    if (secondaryImage) {
      const colorMedia = product.media?.filter(m =>
        m.media_type === 'image' && altColor(m.alt) === selectedColor
      ) ?? [];
      const secondMedia = colorMedia[1];
      if (secondMedia) {
        const newImg = createImageElement(secondMedia, secondaryImage.className, secondaryImage.sizes, product.title);
        secondaryImage.src    = newImg.src;
        secondaryImage.srcset = newImg.srcset;
        secondaryImage.alt    = newImg.alt;
      }
    }
  });
}

function updatePricing(card, variant, colorName) {
  const salePriceEl    = card.querySelector('.price__sale');
  const regularPriceEl = card.querySelector('.price__regular');
  if (!salePriceEl || !regularPriceEl) return;

  const swatch       = card.querySelector(`.color-swatch[title="${colorName}"]`);
  const price        = swatch?.getAttribute('data-price');
  const comparePrice = swatch?.getAttribute('data-compare-price');
  const miniPrice    = card.querySelector('.price.mini-price');
  const isOnSale     = variant.compare_at_price > variant.price;

  if (isOnSale) {
    salePriceEl.style.display    = 'block';
    regularPriceEl.style.display = 'none';
    miniPrice?.classList.add('price--on-sale');

    const comparePriceDiv = salePriceEl.querySelector('.flex_badge_price .compareatprice_active');
    if (comparePriceDiv && price) comparePriceDiv.innerHTML = price;

    const priceDiv = salePriceEl.querySelector('.flex_badge_price dd.price__compare');
    if (priceDiv && comparePrice) {
      priceDiv.innerHTML = `<span class="price-item price-item--regular">${comparePrice}</span>`;
    } else if (!priceDiv && comparePrice) {
      const parent = comparePriceDiv?.closest('dd');
      if (parent) {
        const dd = document.createElement('dd');
        dd.className = 'price__compare';
        dd.innerHTML = `<span class="price-item price-item--regular b4b-vprice">${comparePrice}</span>`;
        parent.after(dd);
      }
    }
  } else {
    salePriceEl.style.display    = 'none';
    regularPriceEl.style.display = 'flex';
    miniPrice?.classList.remove('price--on-sale');

    const regularSpan = regularPriceEl.querySelector('.price-item--regular');
    if (regularSpan && price) {
      regularSpan.innerHTML = price;
    } else if (!regularPriceEl.querySelector('dd') && price) {
      const dd = document.createElement('dd');
      dd.innerHTML = `<span class="price-item price-item--regular b4b-vprice">${price}</span>`;
      regularPriceEl.appendChild(dd);
    }
  }
}

function updateImage(primaryImage, newImage) {
  if (newImage.src === primaryImage.src) return;
  primaryImage.src    = newImage.src;
  primaryImage.srcset = newImage.srcset;
  primaryImage.alt    = newImage.alt;
}

function initSwatchSwipers() {
  if (typeof Swiper === 'undefined') return;

  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      const container = entry.target;
      if (container.swiper) return;
      if (typeof Swiper === 'undefined') {
        window.addEventListener('load', () => {
          if (!container.swiper) new Swiper(container, { slidesPerView: 'auto', spaceBetween: 0, nested: true, observeParents: true });
        }, { once: true });
        return;
      }
      new Swiper(container, { slidesPerView: 'auto', spaceBetween: 0, nested: true, observeParents: true });
    });
  }, { rootMargin: '200px' });

  document.querySelectorAll(
    '.product-recommendations .product-recommendations__product, .collection-product-card'
  ).forEach(card => {
    card.querySelectorAll('.js-color-swatches[data-option-name="color"]').forEach(wrap => {
      const container = wrap.classList.contains('swiper')
        ? wrap
        : wrap.querySelector('.swiper, .swiper-container');
      if (!container || container.swiper) return;
      observer.observe(container);
    });
  });
}

function initProductCards() {
  checkSwatches();
  initSwatchSwipers();
}

document.addEventListener('DOMContentLoaded', initProductCards);
document.addEventListener('shopify:section:load', initProductCards);

window.updateVariantTags = (productCard, tagSelector, specificInput = null) => {
  let currentInput = specificInput;

  if (!currentInput) {
    currentInput = productCard.querySelector(
      '.color-swatch-input:checked, input[data-variant][checked]'
    );
  }

  const currentVariantId = currentInput?.dataset?.variant;
  if (!currentVariantId) return;

  productCard.querySelectorAll(tagSelector).forEach(tag => {
    tag.style.display = 'none';
  });
  const relevantTag = Array.from(productCard.querySelectorAll(tagSelector)).find(tag =>
    tag.getAttribute('data-variant-id') === currentVariantId
  );
  if (relevantTag) relevantTag.style.display = 'block';
};

document.addEventListener('DOMContentLoaded', () => {
  const tagSelectors = [
    '.variant-tag', '.variant-newsize_tag', '.variant-preorder_tag',
    '.new_promo', '.best_promo', '.pref_promo', '.los_promo'
  ];
  document.querySelectorAll('.collection-product-card').forEach(product => {
    tagSelectors.forEach(selector => window.updateVariantTags(product, selector));
  });
});

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.card-wrapper').forEach(card => {
    const firstBadge = card.querySelector('.lowstock-badge');
    if (firstBadge) firstBadge.classList.add('active');
  });
});
